/**
 * Enquiries inbox — the responses submitted through the public website's contact form
 * (partnerships, volunteering, CSR, media, general questions). Office staff triage each
 * one: new → in progress → resolved (or spam). Reply opens the staff email client.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Mail, Phone, Building2, Inbox, Reply, Loader, CheckCircle2, Ban, RefreshCw,
} from 'lucide-react';
import { api } from '../lib/apiClient.js';
import { useToast } from '../components/feedback/ToastProvider.jsx';
import styles from './Enquiries.module.css';

const TOPIC_LABEL = {
  general: 'General',
  partnership: 'Partnership',
  volunteer: 'Volunteer',
  csr: 'CSR',
  media: 'Media',
  donation: 'Donation',
};

const STATUS_FILTERS = [
  ['', 'All'],
  ['new', 'New'],
  ['in_progress', 'In progress'],
  ['resolved', 'Resolved'],
  ['spam', 'Spam'],
];

const STATUS_META = {
  new: { label: 'New', cls: 'stNew' },
  in_progress: { label: 'In progress', cls: 'stProgress' },
  resolved: { label: 'Resolved', cls: 'stResolved' },
  spam: { label: 'Spam', cls: 'stSpam' },
};

export default function Enquiries() {
  const toast = useToast();
  const [filter, setFilter] = useState('');
  const [state, setState] = useState({ status: 'loading', items: [] });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, status: 'loading' }));
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (filter) params.set('status', filter);
      setState({ status: 'ready', items: await api.get(`/enquiries?${params}`) });
    } catch (err) {
      setState({ status: 'error', items: [], error: err.message });
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/enquiries/${id}`, { status });
      toast.success(`Marked ${STATUS_META[status]?.label.toLowerCase()}.`);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const newCount = useMemo(
    () => state.items.filter((e) => e.status === 'new').length,
    [state.items],
  );

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <p className="eyebrow">Supporters</p>
          <h1 className={styles.h1}>
            Enquiries
            {newCount > 0 && <span className={styles.badge}>{newCount} new</span>}
          </h1>
        </div>
        <button className={styles.ghost} onClick={load} title="Refresh">
          <RefreshCw size={15} /> Refresh
        </button>
      </header>

      <div className={styles.filters}>
        {STATUS_FILTERS.map(([v, label]) => (
          <button
            key={v}
            className={`${styles.chip} ${filter === v ? styles.chipOn : ''}`}
            onClick={() => setFilter(v)}
          >
            {label}
          </button>
        ))}
      </div>

      {state.status === 'error' && (
        <div className={styles.error}>
          {state.error} — <button onClick={load}>retry</button>
        </div>
      )}

      {state.status === 'loading' && (
        <div className={styles.list}>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className={styles.skel} />
          ))}
        </div>
      )}

      {state.status === 'ready' && state.items.length === 0 && (
        <div className={styles.empty}>
          <Inbox size={28} />
          <p>No enquiries in this view.</p>
        </div>
      )}

      {state.status === 'ready' && state.items.length > 0 && (
        <div className={styles.list}>
          {state.items.map((e) => {
            const sm = STATUS_META[e.status] || STATUS_META.new;
            return (
              <article key={e.id} className={styles.card}>
                <div className={styles.cardHead}>
                  <div className={styles.who}>
                    <strong>{e.name}</strong>
                    <span className={styles.topic}>{TOPIC_LABEL[e.topic] || e.topic}</span>
                    <span className={`${styles.status} ${styles[sm.cls]}`}>{sm.label}</span>
                  </div>
                  <time className={styles.date}>{new Date(e.createdAt).toLocaleString('en-IN')}</time>
                </div>

                <div className={styles.contacts}>
                  <a href={`mailto:${e.email}`}><Mail size={14} /> {e.email}</a>
                  {e.phone && <a href={`tel:${e.phone}`}><Phone size={14} /> {e.phone}</a>}
                  {e.organisation && <span><Building2 size={14} /> {e.organisation}</span>}
                </div>

                {e.subject && <p className={styles.subject}>{e.subject}</p>}
                <p className={styles.message}>{e.message}</p>

                <div className={styles.actions}>
                  <a
                    className={styles.reply}
                    href={`mailto:${e.email}?subject=${encodeURIComponent(`Re: ${e.subject || 'Your enquiry to USMEHAR CARE'}`)}`}
                  >
                    <Reply size={15} /> Reply
                  </a>
                  <div className={styles.spacer} />
                  {e.status !== 'in_progress' && (
                    <button className={styles.act} onClick={() => setStatus(e.id, 'in_progress')}>
                      <Loader size={14} /> In progress
                    </button>
                  )}
                  {e.status !== 'resolved' && (
                    <button className={styles.actOk} onClick={() => setStatus(e.id, 'resolved')}>
                      <CheckCircle2 size={14} /> Resolve
                    </button>
                  )}
                  {e.status !== 'spam' && (
                    <button className={styles.actBad} onClick={() => setStatus(e.id, 'spam')}>
                      <Ban size={14} /> Spam
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
